import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Named size scale with integer pixel values and matched stroke weights.
 * Thicker strokes at small sizes keep icons legible; thinner strokes
 * at large sizes prevent them from looking heavy.
 */
const ICON_SCALE = {
  xs:  { size: 12, strokeWidth: 2 },
  sm:  { size: 16, strokeWidth: 1.75 },
  md:  { size: 20, strokeWidth: 1.5 },
  lg:  { size: 24, strokeWidth: 1.5 },
  xl:  { size: 32, strokeWidth: 1.5 },
  "2xl": { size: 40, strokeWidth: 1.25 },
} as const;

export type IconSize = keyof typeof ICON_SCALE;

interface AppIconProps {
  /** Any Lucide icon component */
  icon: LucideIcon;
  /** Named size from the scale (default: "md") */
  size?: IconSize;
  /** Override stroke width (use sparingly — the scale handles this) */
  strokeWidth?: number;
  /** CSS color — defaults to "currentColor" */
  color?: string;
  /** SVG fill — defaults to "none" */
  fill?: string;
  /** Additional class names */
  className?: string;
  /** aria-hidden for decorative icons (default: true) */
  "aria-hidden"?: boolean;
  /** Accessible label — set this for standalone meaningful icons */
  "aria-label"?: string;
}

/**
 * Wrapper around Lucide icons that enforces:
 * - `display: block` to eliminate baseline offset jitter
 * - Integer-only sizes from a named scale (no fractional px)
 * - strokeWidth that scales with icon size automatically
 */
export function AppIcon({
  icon: Icon,
  size = "md",
  strokeWidth,
  color,
  fill,
  className,
  "aria-hidden": ariaHidden = true,
  "aria-label": ariaLabel,
}: AppIconProps) {
  const scale = ICON_SCALE[size];

  return (
    <Icon
      size={scale.size}
      strokeWidth={strokeWidth ?? scale.strokeWidth}
      color={color}
      fill={fill ?? "none"}
      className={cn("block shrink-0", className)}
      aria-hidden={ariaLabel ? undefined : ariaHidden}
      aria-label={ariaLabel}
    />
  );
}

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Any Lucide icon component */
  icon: LucideIcon;
  /** Named size from the scale (default: "md") */
  size?: IconSize;
  /** Override stroke width */
  strokeWidth?: number;
  /** Icon color */
  color?: string;
  /** SVG fill */
  fill?: string;
  /** Accessible label (required for icon-only buttons) */
  "aria-label": string;
  /** Additional class names for the button wrapper */
  className?: string;
  /** Additional class names for the icon itself */
  iconClassName?: string;
}

/**
 * Accessible icon-only button with consistent hit target.
 * Minimum 44×44 touch target per WCAG 2.5.8.
 */
export function IconButton({
  icon,
  size = "md",
  strokeWidth,
  color,
  fill,
  "aria-label": ariaLabel,
  className,
  iconClassName,
  ...buttonProps
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg",
        "hover:bg-(--scroll-bg-secondary) transition-colors",
        className,
      )}
      {...buttonProps}
    >
      <AppIcon
        icon={icon}
        size={size}
        strokeWidth={strokeWidth}
        color={color}
        fill={fill}
        className={iconClassName}
        aria-hidden={true}
      />
    </button>
  );
}

/** Re-export the scale for consumers that need raw values (e.g. tab bars) */
export { ICON_SCALE };
